import React from 'react';
import Link from 'components/link';
import styles from './commercialFooterV2Style.module.scss';
import MailIcon from 'components/icon/mail-icon';
import PhoneIcon from 'components/icon/phone-icon';
import Image from 'next/image';

import { EXP_COMMERCIAL_TENANT_ID, FooterConfig, OrganizationConfig } from '@tenants/types/tenant';
import { useTranslation } from 'next-i18next';

export type ExpCommercialFooterProps = {
  config: FooterConfig;
  organizationConfig: OrganizationConfig;
};

export default function CommercialFooterV2({ config, organizationConfig }: ExpCommercialFooterProps) {
  const { t } = useTranslation(EXP_COMMERCIAL_TENANT_ID);

  const { facebookUrl, linkedinUrl, instagramUrl, youtubeUrl, email, phoneNumber, address, companyInfo1, companyInfo2 } = organizationConfig;
  const { footerColumns } = config;
  const { column1 = [], column2 = [], column3 = [], bottom = []} = footerColumns;

  const firstRowLinks = [...column1, ...column2];
  return (
    <footer className={styles.footer} data-testid="exp-commercial-footer">
      <div className={styles.footerContent}>
        <nav className={styles.navigation}>
          <ul>
            {firstRowLinks.map(link => (
              // we use a plain <a> tag here because we want a full page navigation for the benefit of wordpress styling
              <li key={link.label.key}>
                <a href={link.link} key={link.label.key} rel="noreferrer">{t(link.label.key, { defaultValue: link.label.defaultValue })}</a>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.socialMedia}>
          <Link href={facebookUrl} aria-label="Facebook" target="_blank" rel="noreferrer">
            <Image src="/assets/images/facebook.svg" alt="Facebook" width={12} height={22} />
          </Link>
          <Link href={linkedinUrl} aria-label="LinkedIn" target="_blank" rel="noreferrer">
            <Image src="/assets/images/linkedin.svg" alt="LinkedIn" width={22} height={22} />
          </Link>
          <Link href={instagramUrl} aria-label="Instagram" target="_blank" rel="noreferrer">
            <Image src="/assets/images/instagram-outlined.svg" alt="Instagram" width={24} height={24} />
          </Link>
          <Link href={youtubeUrl} aria-label="YouTube" target="_blank" rel="noreferrer">
            <Image src="/assets/images/youtube-outlined.svg" alt="YouTube" width={24} height={24} />
          </Link>
        </div>

        <div className={styles.logo}>
          <Image src="/assets/images/exp-logo.svg" alt="eXp Realty" fill />
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.contactInfo}>
            <address dangerouslySetInnerHTML={{ __html: address() }} />
            <p><PhoneIcon /> <Link href={`tel:${phoneNumber()}`}>{phoneNumber()}</Link></p>
            <p><MailIcon /> <Link href={`mailto:${email()}`}>{email()}</Link></p>
          </div>

          <div className={styles.legalLinks}>
            {column3.map(link => (
              <Link href={link.link} key={link.label.key} rel="noreferrer">{t(link.label.key, { defaultValue: link.label.defaultValue })}</Link>
            ))}
          </div>

          <div className={styles.copyright}>
            <p>© {new Date().getFullYear()} eXp World Holdings, Inc. All Rights Reserved</p>
            <p>eXp Commercial is an eXp World Holdings, Inc. (Nasdaq:EXPI) company.</p>
          </div>

          <div className={styles.legalText}>
            <p>eXp Commercial is an eXp World Holdings, Inc. (Nasdaq: EXPI) company.</p>
            <p>{companyInfo1}</p>
            <p>{companyInfo2}</p>
            {bottom.slice(0, 2).map(section => (
              <p key={section.map(link => link.label.key).join('-')}>
                {section.map(link => (
                  <Link href={link.link} key={link.label.key} rel="noreferrer">{t(link.label.key, { defaultValue: link.label.defaultValue })}</Link>
                ))}
              </p>
            ))}
          </div>
        </div>

      </div>
    </footer>
  );
}

